import { z } from 'zod';

/**
 * Domain validation schema
 * Validates domain format: domain.com, subdomain.domain.com
 * Rules:
 * - Labels are separated by dots
 * - Each label can contain letters (a-z), digits (0-9), and hyphens (-)
 * - Labels CANNOT start or end with a hyphen
 * - No protocols (http://, https://)
 * - No paths (/)
 * - Case insensitive (auto-converts to lowercase)
 */
export const domainSchema = z
  .string()
  .min(1, 'Domain is required')
  .transform((value) => value.trim().toLowerCase())
  .refine(
    (value) => {
      // Check if it contains protocol (http:// or https://)
      if (value.includes('://')) {
        return false;
      }
      return true;
    },
    {
      message: 'Domain should not include http:// or https://',
    }
  )
  .refine(
    (value) => {
      // Check if it contains paths (/)
      if (value.includes('/')) {
        return false;
      }
      return true;
    },
    {
      message: 'Domain should not include paths (e.g., /page)',
    }
  )
  .refine(
    (value) => {
      // Must have at least one dot for a valid domain
      if (!value.includes('.')) {
        return false;
      }
      return true;
    },
    {
      message: 'Domain must include a TLD (e.g., .com, .net)',
    }
  )
  .refine(
    (value) => {
      // Validate each label in the domain
      const labels = value.split('.');

      // Must have at least 2 labels (domain + TLD)
      if (labels.length < 2) {
        return false;
      }

      // Label validation regex: alphanumeric and hyphens only
      const labelCharsRegex = /^[a-zA-Z0-9-]+$/;

      for (const label of labels) {
        // Label cannot be empty
        if (label.length === 0) {
          return false;
        }
        // Label cannot start with hyphen
        if (label.startsWith('-')) {
          return false;
        }
        // Label cannot end with hyphen
        if (label.endsWith('-')) {
          return false;
        }
        // Label can only contain alphanumeric and hyphens
        if (!labelCharsRegex.test(label)) {
          return false;
        }
      }

      return true;
    },
    {
      message:
        'Please enter a valid domain format (e.g., domain.com). Labels cannot start or end with hyphens.',
    }
  );

export type DomainValidationResult = {
  isValid: boolean;
  error?: string;
};

/**
 * Validates a domain string
 * @param domain The domain to validate
 * @returns Validation result with isValid flag and optional error message
 */
export const validateDomain = (domain: string): DomainValidationResult => {
  const result = domainSchema.safeParse(domain);
  if (result.success) {
    return { isValid: true };
  }
  return {
    isValid: false,
    error: result.error.errors[0]?.message || 'Invalid domain format',
  };
};
